import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { SalonPageShell } from '@/components/layout/SalonPageShell'
import { BookingMessagesSection } from '@/components/settings/BookingMessagesSection'
import { HoursSettingsSection } from '@/components/settings/HoursSettingsSection'
import { LoyaltySettingsSection } from '@/components/settings/LoyaltySettingsSection'
import { PaymentLabelsSection } from '@/components/settings/PaymentLabelsSection'
import { ReceiptSettingsSection } from '@/components/settings/ReceiptSettingsSection'
import { RemindersSettingsSection } from '@/components/settings/RemindersSettingsSection'
import { SalonSettingsSection } from '@/components/settings/SalonSettingsSection'
import { UsersSettingsSection } from '@/components/settings/UsersSettingsSection'
import { AuditLogPage } from '@/pages/admin/AuditLogPage'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { hasEffectivePermission, hasPermission, PERMISSIONS } from '@/lib/permissions'
import { SALON_DESK_SETTINGS } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

export function SettingsPage() {
  const { user } = useAuth()
  const canManageSalon = hasEffectivePermission(user, PERMISSIONS.MENU_SETTINGS)
  const canManageTeam =
    hasPermission(user, PERMISSIONS.ADMIN_USERS) || hasPermission(user, PERMISSIONS.ADMIN_FULL)
  const canViewAudit =
    hasPermission(user, PERMISSIONS.ADMIN_FULL) || hasPermission(user, PERMISSIONS.ACCOUNTS_ACCESS)

  const tabs = useMemo(() => {
    const items = []

    if (canManageSalon) {
      items.push({ id: 'salon', label: 'Salon' })
      items.push({ id: 'hours', label: 'Hours' })
    }

    if (canManageTeam) {
      items.push({ id: 'team', label: 'Team' })
    }

    if (canManageSalon && SALON_DESK_SETTINGS.showBookingMessagesInSettings) {
      items.push({ id: 'messages', label: 'Messages' })
    }

    if (canManageSalon && SALON_DESK_SETTINGS.showLoyaltySettings) {
      items.push({ id: 'loyalty', label: 'Loyalty' })
    }

    if (canManageSalon && SALON_DESK_SETTINGS.showRemindersSection) {
      items.push({ id: 'reminders', label: 'Reminders' })
    }

    if (canViewAudit && SALON_DESK_SETTINGS.showAuditLog) {
      items.push({ id: 'audit', label: 'Audit' })
    }

    if (canManageSalon && SALON_DESK_SETTINGS.showAdvancedSettingsSections) {
      items.push({ id: 'payments', label: 'Payments' })
      items.push({ id: 'receipt', label: 'Receipt' })
    }

    return items
  }, [canManageSalon, canManageTeam, canViewAudit])

  const [activeTab, setActiveTab] = useState(() => tabs[0]?.id ?? 'team')

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id ?? 'team')
    }
  }, [activeTab, tabs])

  const showTabs = tabs.length > 1

  return (
    <SalonPageShell
      title="Settings"
      description="Salon details, hours, team, loyalty, and more — one section per tab."
    >
      {tabs.length === 0 ? (
        <p className="text-sm text-muted-foreground">You do not have permission to change settings.</p>
      ) : (
        <>
          {showTabs ? (
            <div className="mb-4 inline-flex max-w-full flex-wrap rounded-2xl border border-border/60 bg-muted/30 p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  data-settings-tab={tab.id}
                  className={cn(
                    'rounded-xl px-4 py-2 text-sm font-medium transition-colors',
                    activeTab === tab.id
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ) : null}

          <div className={activeTab === 'audit' ? 'max-w-5xl' : 'max-w-3xl'}>
            {activeTab === 'salon' ? <SalonSettingsSection embedded /> : null}
            {activeTab === 'hours' ? <HoursSettingsSection embedded /> : null}
            {activeTab === 'team' ? <UsersSettingsSection embedded /> : null}
            {activeTab === 'messages' ? <BookingMessagesSection embedded /> : null}
            {activeTab === 'loyalty' ? <LoyaltySettingsSection embedded /> : null}
            {activeTab === 'reminders' ? <RemindersSettingsSection embedded /> : null}
            {activeTab === 'audit' ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-muted-foreground">
                    Recent admin actions for this salon. Open the full page for export filters.
                  </p>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/admin/audit-logs">Open audit log page</Link>
                  </Button>
                </div>
                <AuditLogPage embedded />
              </div>
            ) : null}
            {activeTab === 'payments' ? <PaymentLabelsSection embedded /> : null}
            {activeTab === 'receipt' ? <ReceiptSettingsSection embedded /> : null}
          </div>
        </>
      )}
    </SalonPageShell>
  )
}
